import { palette } from "../../app/theme";
import type { MaterialUsage } from "./serviceMaterials";

type SupplyCategory = keyof typeof palette.supplyCategory;

// Catalog order of the categories; supplies without a category come last.
const CATEGORY_ORDER = Object.keys(palette.supplyCategory) as SupplyCategory[];

const isKnownCategory = (category: string): category is SupplyCategory =>
  category in palette.supplyCategory;

export const supplyCategoryColor = (category: string | null | undefined): string =>
  category && isKnownCategory(category)
    ? palette.supplyCategory[category]
    : palette.supplyCategoryNone;

// Category labels share the Settings → Supplies translations.
export const supplyCategoryMessageId = (category: string | null | undefined) =>
  category
    ? `material_category_${category.toLowerCase()}`
    : "ticket_supplies_uncategorized";

export type SupplyCategoryGroup<Usage> = {
  category: string | null;
  supplies: Usage[];
};

export function groupSuppliesByCategory<Usage extends MaterialUsage>(
  supplies: Usage[],
  categoryByMaterialId: Record<string, string | null | undefined>
): SupplyCategoryGroup<Usage>[] {
  const byCategory = new Map<string | null, Usage[]>();
  for (const supply of supplies) {
    const category = (supply.material && categoryByMaterialId[supply.material]) || null;
    byCategory.set(category, [...(byCategory.get(category) ?? []), supply]);
  }
  const rank = (category: string | null) => {
    const index = category ? CATEGORY_ORDER.indexOf(category as SupplyCategory) : -1;
    return index === -1 ? CATEGORY_ORDER.length : index;
  };
  return [...byCategory.entries()]
    .sort(([first], [second]) => rank(first) - rank(second))
    .map(([category, groupSupplies]) => ({ category, supplies: groupSupplies }));
}
